import { setUserPersonalization } from "../../../store/slices/personalizationSlice";
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from "../../storage/localStorageUtil";
import { getSessionStorage, setSessionStorage } from "../../storage/sessionStorageUtil";

const PERSONALIZATION_SKIPPED_STORAGE_PREFIX = "personalizationSkipped_";

/**
 * This method is used to validate extension
 *
 * @param {string} allowedMediaType comma separated allowed media types.
 * @param {string} currentMediaType current media type.
 * @returns {boolean} return valid or false.
 */
export function validateExtension(allowedMediaType, currentMediaType) {
  const mediaTypeToArray = allowedMediaType.split(", ");
  return mediaTypeToArray.indexOf(currentMediaType) > -1;
}
/**
 * This method brings component to add personalized image
 *
 * @param {string} minSize minimum size of file.
 * @param {string} minUnit minimum size file unit.
 * @param {string} maxSize maximum size file unit.
 * @param {string} maxUnit maximum size file unit.
 * @param {number} fileSize current file size.
 * @returns {void} jsx for the personalized image.
 */
export const isValidFileSize = (minSize, minUnit, maxSize, maxUnit, fileSize) => {
  const MB = 1024 * 1024;
  const KB = 1024;
  const minFileSize = minSize * (minUnit === "KB" ? KB : MB);
  const maxFileSize = maxSize * (maxUnit === "MB" ? MB : KB);
  return fileSize > minFileSize && fileSize < maxFileSize;
};
/**
 * This method is used to lead number with zero
 *
 * @param {number} number number.
 * @returns {string} return modified number
 */
export const leadingZero = (number) => {
  return number <= 9 ? `0${number}` : number;
};
/**
 * This method is used to get foldername of date
 *
 * @returns {string} return date folder
 */
export const getFolderDirectory = () => {
  const currentDate = new Date();
  const date = currentDate.getDate();
  const month = currentDate.getMonth() + 1;
  const year = currentDate.getFullYear();
  return `${year}${leadingZero(month)}${leadingZero(date)}`;
};
/**
 * This method is used to generate random character with specified length
 *
 * @param {number} size length of random characters.
 * @returns {string} return random characters
 */
export const generateRandomCharacters = (size) => {
  let generatedOutput = "";
  const storedCharacters = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const totalCharacterSize = storedCharacters.length;
  for (let index = 0; index < size; index += 1) {
    generatedOutput += storedCharacters.charAt(Math.floor(Math.random() * totalCharacterSize));
  }
  return generatedOutput;
};

/**
 * This method brings component to add personalized image
 *
 * @param {string} filename name of file.
 * @returns {string} file path.
 */
export const generateFilePath = (filename) => {
  return `pz/${getFolderDirectory()}/${generateRandomCharacters(10)}-${filename}`;
};

/**
 * Returns the supported file types based on the allowed media types.
 *
 * @param {string} allowedMediaType - A comma-separated string of allowed media types (e.g., "image/jpeg,image/png").
 * @returns {string} The supported file extensions (e.g., "JPEG, PNG").
 */
export const getSupportedFileType = (allowedMediaType) => {
  const data = allowedMediaType?.split(",");
  const extensions = [];
  data?.forEach((element) => {
    const [, extension] = element.split("/");
    extensions.push(extension.toUpperCase());
  });
  return extensions.join(", ");
};

/**
 * This method is used to prepare image configs as per the input required
 *
 * @param {string} maxInputRequired max number of images to configure
 * @returns {object} returns the initial structure of the multi image upload
 */
export const imageCountConfig = (maxInputRequired) => {
  const configs = [];
  Array(parseInt(maxInputRequired, 10))
    .fill("")
    .map((_, index) => {
      configs.push({
        uploading: false,
        uploaded: false,
        sizeError: false,
        uploadError: false,
        imgUrl: null,
        id: index,
      });
      return configs;
    });
  return configs;
};

/**
 * This method is used to check uploaded image count
 *
 * @returns {number} uploaded image count.
 */
export const checkMultiUploadedImageCount = (imageConfigs) => {
  const uploaded = imageConfigs.filter((c) => c.imgUrl !== null);
  return uploaded?.length;
};

/**
 * Returns whether a single personalization value is non-empty.
 *
 * @param {*} value - Personalization value to check.
 * @returns {boolean} Whether the value is usable.
 */
export const isNonEmptyPersonalizationValue = (value) => {
  return value !== null && value !== undefined && value !== "";
};

/**
 * Filters out empty personalization values from an array.
 *
 * @param {Array} values - Personalization values.
 * @returns {Array} Non-empty values only.
 */
export const getValidPersonalizationValues = (values = []) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter(isNonEmptyPersonalizationValue);
};

/**
 * Checks if a personalization entry has at least one non-empty value.
 *
 * @param {Object} entry - Personalization entry to validate.
 * @returns {boolean} Whether the entry has a usable value.
 */
export const hasValidPersonalizationValue = (entry) => {
  return getValidPersonalizationValues(entry?.value).length > 0;
};

/**
 * Persists personalization array to Redux and localStorage.
 * Clears localStorage when no valid entries remain.
 *
 * @param {string} productId - Product id used as the localStorage key.
 * @param {Array} personalizationArray - Personalization entries to persist.
 * @param {Function} dispatch - Redux dispatch function.
 * @returns {Array} The filtered list of valid entries that were persisted.
 */
export const persistUserPersonalization = (productId, personalizationArray, dispatch) => {
  const validEntries = (Array.isArray(personalizationArray) ? personalizationArray : []).filter(
    hasValidPersonalizationValue,
  );

  if (validEntries.length === 0) {
    removeLocalStorageItem(productId);
    dispatch(setUserPersonalization([]));
    return [];
  }

  setLocalStorageItem([productId], JSON.stringify(validEntries));
  dispatch(setUserPersonalization(validEntries));
  return validEntries;
};

/**
 *
 * @param {*} productId
 * @param {*} dispatch
 */
export const handlePersonalizationDetailsOnRefresh = (productId) => {
  let getDetailsFromLocalStorage;
  try {
    getDetailsFromLocalStorage = JSON.parse(getLocalStorageItem(productId)) || {};
  } catch (error) {
    getDetailsFromLocalStorage = {}; // Fallback to an empty object if parsing fails
  }
  return getDetailsFromLocalStorage;
};

/**
 * Updates or adds a personalization item in the user personalization state.
 *
 * This function checks if a personalization with the same `fieldName` exists in the current
 * `userPersonalization` array. If it does, the existing item is updated with the new value;
 * otherwise, the new personalization is added to the array. Entries with empty values are removed.
 *
 * @param {Object} personalizationValue - The new personalization value to add or update.
 * @param {Array} userPersonalization - The current list of user personalizations.
 * @param {Function} dispatch - The dispatch function to update the personalization state.
 */
export const handleSetUserPersonalizations = (productId, personalizationValue, userPersonalization, dispatch) => {
  let currentPersonalization = Array.isArray(userPersonalization) ? userPersonalization : [];

  if (currentPersonalization.length === 0) {
    const savedPersonalization = handlePersonalizationDetailsOnRefresh(productId);
    if (Array.isArray(savedPersonalization) && savedPersonalization.length > 0) {
      currentPersonalization = savedPersonalization;
    }
  }

  if (Array.isArray(personalizationValue)) {
    persistUserPersonalization(productId, personalizationValue, dispatch);
    return;
  }

  const existingPersonalizationIndex = currentPersonalization.findIndex(
    (personalization) => personalization.fieldName === personalizationValue?.fieldName,
  );

  let updatedPersonalization;

  if (existingPersonalizationIndex >= 0) {
    updatedPersonalization = [...currentPersonalization];
    if (hasValidPersonalizationValue(personalizationValue)) {
      updatedPersonalization[existingPersonalizationIndex] = personalizationValue;
    } else {
      updatedPersonalization.splice(existingPersonalizationIndex, 1);
    }
  } else if (hasValidPersonalizationValue(personalizationValue)) {
    updatedPersonalization = [...currentPersonalization, personalizationValue];
  } else {
    updatedPersonalization = [...currentPersonalization];
  }

  persistUserPersonalization(productId, updatedPersonalization, dispatch);
};

/**
 * Validates the personalization data against user inputs.
 * @param {object} personlizedData - The data for the personalized product.
 * @param {Array} userPersonalization - The user's personalization inputs.
 * @returns {boolean} Returns `true` if validation passes or if no validation is required, otherwise `false`.
 */
export const checkPersonalizationValidation = (personlizedData, userPersonalization) => {
  const personalizationOptions = personlizedData?.personalizedOptions;
  if (!personlizedData?.personalizedProduct || personalizationOptions?.[0]?.required === "N") {
    // No personalized product, validation not required
    return true;
  }

  // Check if lengths are the same
  // if (personalizationOptions.length !== userPersonalization.length) {
  //   return false;
  // }

  // Check each personalization option
  return personalizationOptions.every((option) => {
    const userOption = userPersonalization.find((user) => user.fieldName === option.fieldName);

    if (!userOption || !Array.isArray(userOption.value)) {
      return false;
    }

    const validValues = getValidPersonalizationValues(userOption.value);
    if (validValues.length === 0) {
      return false;
    }

    const isImageOrAudio = option.contentType === "IMAGE" || option.contentType === "AUDIO";
    if (isImageOrAudio) {
      const minRequired = Math.max(parseInt(option.minInputRequired, 10) || 1, 1);
      return validValues.length >= minRequired;
    }

    return validValues.length > 0;
  });
};

/**
 * Builds CleverTap personalization stats from product config (not uploaded totals).
 * image_count → personalizedDetails.noOfImgs
 * text_count → TEXT options under personalizedInfo.personalizedOptions
 *
 * @param {Object} [currentItem] - Current PDP product.
 * @returns {{ is_text: boolean, is_image: boolean, image_count: number, text_count: number }}
 */
export const getPersonalizationCompletionAnalytics = (currentItem) => {
  const imageCount = Number(currentItem?.personalizedDetails?.noOfImgs) || 0;
  const personalizedOptions = currentItem?.personalizedInfo?.personalizedOptions;
  const textCount = Array.isArray(personalizedOptions)
    ? personalizedOptions.filter((option) => option?.contentType === "TEXT").length
    : 0;

  return {
    is_text: textCount > 0,
    is_image: imageCount > 0,
    image_count: imageCount,
    text_count: textCount,
  };
};

/**
 * Checks if personalization is complete for a specific product using its saved local storage data.
 * Presence-based: any non-empty saved entry under this productId counts as complete. Per-field
 * validation isn't used here because the WYSIWYG template editor's own save doesn't necessarily
 * echo back one message per field it was given, so strict validation can under-count a save the
 * editor itself already reported as successful.
 * @param {string} productId - The product id to check saved personalization for.
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Returns `true` if any saved personalization exists for this productId.
 */
export const isProductPersonalizationComplete = (productId, personalizedInfo) => {
  if (!personalizedInfo?.personalizedProduct) {
    return false;
  }

  const savedPersonalization = handlePersonalizationDetailsOnRefresh(productId);
  return Array.isArray(savedPersonalization) && savedPersonalization.length > 0;
};

/**
 * Checks whether the product only has text personalization options.
 *
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Whether all personalization options are text fields.
 */
export const isTextOnlyPersonalization = (personalizedInfo) => {
  const personalizationOptions = personalizedInfo?.personalizedOptions;
  if (!Array.isArray(personalizationOptions) || personalizationOptions.length === 0) {
    return false;
  }

  return personalizationOptions.every((option) => option.contentType === "TEXT");
};

/**
 * Checks whether the user has started text personalization (saved at least once).
 *
 * @param {string} productId - The product id used as the localStorage key.
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Whether any text field exists in saved personalization data.
 */
export const hasStartedTextPersonalization = (productId, personalizedInfo) => {
  const savedPersonalization = handlePersonalizationDetailsOnRefresh(productId);
  if (!Array.isArray(savedPersonalization) || savedPersonalization.length === 0) {
    return false;
  }

  const textFieldNames = personalizedInfo?.personalizedOptions
    ?.filter((option) => option.contentType === "TEXT")
    .map((option) => option.fieldName);

  return savedPersonalization.some(
    (entry) => entry.contentType === "TEXT" && textFieldNames?.includes(entry.fieldName),
  );
};

/**
 * Whether a product's customization is optional, i.e. no field is individually mandatory.
 * Mirrors the product-level shortcut already used by `checkPersonalizationValidation`, so the
 * "optional" determination stays consistent between add-to-cart validation and CTA visibility.
 *
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Whether customization is optional for this product.
 */
export const isPersonalizationOptional = (personalizedInfo) => {
  return personalizedInfo?.personalizedOptions?.[0]?.required === "N";
};

/**
 * Whether this product's personalization is text-only and fully optional, i.e. eligible for an
 *
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Whether to render personalization inline instead of via drawer.
 */
export const isInlineOptionalPersonalization = (personalizedInfo) => {
  const personalizedOptions = personalizedInfo?.personalizedOptions;
  if (!Array.isArray(personalizedOptions) || personalizedOptions.length === 0 || personalizedOptions.length > 2) {
    return false;
  }

  return personalizedOptions.every((option) => option?.contentType === "TEXT" && option?.required !== "Y");
};

/**
 * Marks that the user has explicitly resolved (closed or continued past) optional customization
 * for this product, for the current browser session only.
 *
 * @param {string} productId - The product id used as the sessionStorage key.
 * @returns {void}
 */
export const markPersonalizationSkipped = (productId) => {
  setSessionStorage(`${PERSONALIZATION_SKIPPED_STORAGE_PREFIX}${productId}`, true);
};

/**
 * Whether the user has already resolved (closed or continued past) optional customization for
 * this product during the current browser session.
 *
 * @param {string} productId - The product id used as the sessionStorage key.
 * @returns {boolean} Whether personalization was skipped this session.
 */
export const isPersonalizationSkipped = (productId) => {
  return getSessionStorage(`${PERSONALIZATION_SKIPPED_STORAGE_PREFIX}${productId}`) === true;
};

/**
 * Whether the PDP footer should show the "Customise now" CTA for this product.
 * Text-only products keep inline fields after the first save; validation runs on add to cart.
 * Once a session has resolved optional customization (saved, or explicitly skipped via close /
 * Save & Continue with nothing entered), the button stays hidden for the rest of the session.
 *
 * @param {string} productId - The product id used as the localStorage key.
 * @param {object} personalizedInfo - The personalized info object from the current product.
 * @returns {boolean} Whether to show the customise-now footer button.
 */
export const shouldShowPersonalizationCustomiseButton = (productId, personalizedInfo) => {
  if (!personalizedInfo?.personalizedProduct) {
    return false;
  }

  if (isProductPersonalizationComplete(productId, personalizedInfo)) {
    return false;
  }

  if (isPersonalizationOptional(personalizedInfo) && isPersonalizationSkipped(productId)) {
    return false;
  }

  if (isTextOnlyPersonalization(personalizedInfo) && hasStartedTextPersonalization(productId, personalizedInfo)) {
    return false;
  }

  return true;
};
